import { useMemo, useState } from 'react'

import Alert from 'react-bootstrap/Alert'
import Button from 'react-bootstrap/Button'
import Form from 'react-bootstrap/Form'
import Modal from 'react-bootstrap/Modal'
import Stack from 'react-bootstrap/Stack'

import { constants } from '@blocktickets/shared-utils'

import { Confirm } from '..'
import { Spinner } from '../../LoadingContainer/Spinner'

import { useModal } from '../../../providers/ModalProvider/ModalProvider'
import { removeTicketsFromEventOrder } from '../../../utilities/api'
import { formatCurrency, isCashOrCheckOrder } from '../../../utilities/helpers'

const sortTickets = (tickets = []) => {
  const safe = (v) => String(v ?? '')
  return [...tickets].sort((a, b) => {
    const aKey = `${safe(a.sectionNumber)}-${safe(a.rowNumber)}-${safe(a.seatNumber)}-${safe(a.checkInCode)}-${a.id}`
    const bKey = `${safe(b.sectionNumber)}-${safe(b.rowNumber)}-${safe(b.seatNumber)}-${safe(b.checkInCode)}-${b.id}`
    return aKey.localeCompare(bKey, undefined, { numeric: true })
  })
}

const formatEventTicket = (ticket) => {
  const isGa = ticket?.generalAdmission || ticket?.GA
  const seatLabel = isGa
    ? `Sec ${ticket.sectionNumber || 'GA'} (GA)`
    : `Sec ${ticket.sectionNumber}, Row ${ticket.rowNumber}, Seat ${ticket.seatNumber}`
  return ticket?.checkInCode
    ? `${seatLabel} · ${ticket.checkInCode}`
    : seatLabel
}

const roundMoney = (value) => Math.round(Number(value || 0) * 100) / 100

export default function RemoveEventTickets({ order, onSuccess }) {
  const { closeModal, openModal } = useModal()
  const { ORDER_STATUS } = constants

  const tickets = useMemo(
    () => sortTickets(order?.tickets || []),
    [order?.tickets]
  )

  const maxSelectable = Math.max(0, tickets.length - 1)

  const [selectedIds, setSelectedIds] = useState(new Set())
  const [selectCount, setSelectCount] = useState(
    String(Math.min(1, maxSelectable) || '')
  )
  const [issueRefund, setIssueRefund] = useState(false)
  const [refundAmount, setRefundAmount] = useState('')
  const [isSaving, setIsSaving] = useState(false)
  const [alert, setAlert] = useState({ show: false, message: '' })

  const hasPaymentIntent =
    order?.paymentIntentId && String(order.paymentIntentId).trim() !== ''

  const cashOrCheck = isCashOrCheckOrder(order)
  const paidStatus =
    order?.status === ORDER_STATUS.COMPLETE ||
    order?.status === ORDER_STATUS.COMPLETE_FROM_ASSIGNMENT
  const canOfferStripeRefund = hasPaymentIntent && !cashOrCheck && paidStatus
  const canReduceCashOrCheckTotal = cashOrCheck && paidStatus
  const canOfferRefund = canOfferStripeRefund || canReduceCashOrCheckTotal

  const selectedTickets = useMemo(
    () => tickets.filter((ticket) => selectedIds.has(ticket.id)),
    [tickets, selectedIds]
  )

  const selectedCount = selectedTickets.length
  const orderTotal = roundMoney(order?.total)
  const suggestedRefundAmount =
    tickets.length > 0 && selectedCount > 0
      ? roundMoney((orderTotal / tickets.length) * selectedCount)
      : 0

  const resolvedRefundAmount = roundMoney(
    refundAmount === '' ? suggestedRefundAmount : refundAmount
  )

  const toggleTicket = (ticketId) => {
    const next = new Set(selectedIds)
    if (next.has(ticketId)) next.delete(ticketId)
    else if (next.size < maxSelectable) next.add(ticketId)
    setSelectedIds(next)
  }

  const selectFirstN = () => {
    const count = Math.min(
      maxSelectable,
      Math.max(0, Math.round(Number(selectCount) || 0))
    )
    setSelectedIds(new Set(tickets.slice(0, count).map((ticket) => ticket.id)))
  }

  const handleRefundToggle = (checked) => {
    setIssueRefund(checked)
    if (checked && refundAmount === '') {
      setRefundAmount(String(suggestedRefundAmount || ''))
    }
  }

  const openConfirm = () => {
    if (!selectedCount || selectedCount >= tickets.length) return

    const ticketLabels = selectedTickets
      .map((ticket) => formatEventTicket(ticket))
      .join('; ')

    let description = `Remove ${selectedCount} ticket${
      selectedCount === 1 ? '' : 's'
    } from this order and release them back to inventory? Tickets: ${ticketLabels}. Printed QR codes on returned tickets will stop scanning.`

    if (issueRefund && canOfferRefund) {
      description += cashOrCheck
        ? ` The order total will be reduced by ${formatCurrency(
            resolvedRefundAmount
          )}. Return that amount by cash or check outside the system.`
        : ` A partial refund of ${formatCurrency(
            resolvedRefundAmount
          )} will be issued to the original payment method, including a proportional share of BlockTickets service fees.`
    } else {
      description +=
        ' Order totals and service fees will be reduced for the remaining tickets.'
    }

    openModal(
      Confirm,
      {
        title: 'Remove selected tickets?',
        description,
        onConfirm: () => {
          closeModal()
          handleRemove()
        },
        onClose: closeModal,
      },
      { className: 'modal-md', contentClassName: '' },
      { replace: false }
    )
  }

  const handleRemove = async () => {
    try {
      setIsSaving(true)
      setAlert({ show: false, message: '' })

      const payload = {
        orderId: order?.id,
        ticketIds: Array.from(selectedIds),
      }

      if (issueRefund && canOfferRefund) {
        payload.processRefund = true
        payload.refundAmount = resolvedRefundAmount
      }

      await removeTicketsFromEventOrder(payload)

      if (typeof onSuccess === 'function') onSuccess()
      closeModal()
    } catch (err) {
      setAlert({
        show: true,
        message:
          err?.response?.data?.error?.message ||
          err?.response?.data?.message ||
          'Unable to remove tickets. Please try again.',
      })
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <Modal.Body className="d-flex-column">
      <div className="modal-body-heading">
        <div className="section-heading section-heading--secondary">
          <h1 className="mt-2 mb-0">Remove Tickets</h1>
          <p className="section-header-desc mt-1 mb-0">
            Select unsold or returned tickets to release back to inventory.
            Printed tickets will stop scanning because their check-in codes are
            regenerated. To return the entire order, use Refund or Release
            instead.
          </p>
        </div>
      </div>

      {alert.show && (
        <Alert variant="danger" className="mb-3">
          <p className="mb-0">{alert.message}</p>
        </Alert>
      )}

      <Stack gap={3}>
        {maxSelectable > 1 && (
          <Form.Group controlId="remove-event-tickets-select-n">
            <Form.Label>Select unused count</Form.Label>
            <Stack direction="horizontal" gap={2}>
              <Form.Control
                type="number"
                min="1"
                max={maxSelectable}
                value={selectCount}
                onChange={(event) => setSelectCount(event.target.value)}
              />
              <Button variant="outline-light" onClick={selectFirstN}>
                Select
              </Button>
            </Stack>
            <p className="small text-muted mt-1 mb-0">
              Selects the first {maxSelectable === 1 ? 'ticket' : 'N tickets'}{' '}
              in the list. Leave at least one ticket on the order.
            </p>
          </Form.Group>
        )}

        <Stack gap={2}>
          <h3 className="small-bold mb-0">Current Tickets</h3>
          <div style={{ maxHeight: '280px', overflowY: 'auto' }}>
            {tickets.map((ticket) => (
              <Form.Check
                key={ticket.id}
                id={`remove-event-ticket-${ticket.id}`}
                checked={selectedIds.has(ticket.id)}
                onChange={() => toggleTicket(ticket.id)}
                className="mb-2"
                label={formatEventTicket(ticket)}
              />
            ))}
          </div>
        </Stack>

        <div className="small">
          Selected to remove: <strong>{selectedCount}</strong> ticket
          {selectedCount === 1 ? '' : 's'}
        </div>

        {canOfferRefund && selectedCount > 0 && (
          <Stack gap={2}>
            <Form.Check
              id="remove-event-tickets-issue-refund"
              type="switch"
              label={
                cashOrCheck ? 'Reduce order total' : 'Issue partial refund'
              }
              checked={issueRefund}
              onChange={(event) => handleRefundToggle(event.target.checked)}
            />
            {issueRefund && (
              <>
                <Form.Group controlId="remove-event-tickets-refund-amount">
                  <Form.Label>
                    {cashOrCheck
                      ? 'Amount to remove from the total'
                      : 'Refund amount'}
                  </Form.Label>
                  <Form.Control
                    type="number"
                    min="0"
                    step="0.01"
                    value={refundAmount}
                    onChange={(event) => setRefundAmount(event.target.value)}
                    placeholder={String(suggestedRefundAmount || '0')}
                  />
                </Form.Group>
                <p className="small text-muted mb-0">
                  Suggested amount based on order total:{' '}
                  {formatCurrency(suggestedRefundAmount)}.{' '}
                  {cashOrCheck
                    ? 'This lowers the recorded order total. Return that amount by cash or check outside the system.'
                    : 'This includes a proportional share of BlockTickets service fees. Stripe card processing fees are not returned.'}
                </p>
              </>
            )}
          </Stack>
        )}
      </Stack>

      <Stack
        direction="horizontal"
        className="btn-group-flex justify-content-end mt-4"
      >
        <Button variant="default" size="lg" onClick={closeModal}>
          Cancel
        </Button>
        <Button
          size="lg"
          variant="outline-light"
          className="text-danger"
          disabled={
            !selectedCount || selectedCount >= tickets.length || isSaving
          }
          onClick={openConfirm}
        >
          {isSaving ? <Spinner /> : 'Remove Tickets'}
        </Button>
      </Stack>
    </Modal.Body>
  )
}
